import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Form, Input, Modal, Popconfirm, Spin } from 'antd';
import {
  BookOutlined,
  DeleteOutlined,
  PlusOutlined,
  TableOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import type { CreateDictionaryRequest, Dictionary, DictionaryListItem } from '@lens/shared';
import { ApiError } from '../../shared/api/client.ts';
import { createDictionary, deleteDictionary, listDictionaries } from './api.ts';
import styles from './DictionariesPage.module.css';
import { useDocumentTitle } from '../../shared/hooks/useDocumentTitle.ts';

interface State {
  items: DictionaryListItem[] | null;
  loading: boolean;
  error: string | null;
}

function toListItem(dictionary: Dictionary): DictionaryListItem {
  return {
    id: dictionary.id,
    name: dictionary.name,
    description: dictionary.description,
    createdById: dictionary.createdById,
    ownerId: dictionary.ownerId,
    owner: dictionary.owner,
    keyColumnId: dictionary.keyColumnId,
    displayColumnId: dictionary.displayColumnId,
    createdAt: dictionary.createdAt,
    updatedAt: dictionary.updatedAt,
    columnCount: dictionary.columns.length,
    entryCount: 0,
  };
}

export function DictionariesPage(): JSX.Element {
  useDocumentTitle('Справочники');
  const navigate = useNavigate();
  const [form] = Form.useForm<CreateDictionaryRequest>();
  const [state, setState] = useState<State>({
    items: null,
    loading: true,
    error: null,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const handleDelete = useCallback(async (id: string) => {
    setDeleting(id);
    try {
      await deleteDictionary(id);
      setState((current) => ({
        ...current,
        items: current.items ? current.items.filter((i) => i.id !== id) : null,
      }));
    } catch (error) {
      Modal.error({
        title: 'Не удалось удалить справочник',
        content: error instanceof ApiError ? error.message : 'Возможно, справочник используется в полях',
      });
    } finally {
      setDeleting(null);
    }
  }, []);

  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: null }));
    try {
      // scope=tab → сервер вернёт только видимые текущему пользователю
      // справочники (создатель / владелец / редактор / наблюдатель; админу —
      // все). Ограничение касается только этой вкладки.
      const items = await listDictionaries('tab');
      setState({ items, loading: false, error: null });
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message || 'Не удалось загрузить список справочников'
          : 'Не удалось загрузить список справочников';
      setState({ items: null, loading: false, error: message });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    form.resetFields();
  }, [form]);

  const handleCreate = useCallback(
    async (values: CreateDictionaryRequest) => {
      setSubmitting(true);
      try {
        const created = await createDictionary(values);
        setState((current) => ({
          ...current,
          items: current.items ? [...current.items, toListItem(created)] : [toListItem(created)],
        }));
        closeModal();
      } finally {
        setSubmitting(false);
      }
    },
    [closeModal],
  );

  if (state.loading) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', padding: 64 }}
        data-testid="dictionaries-page"
      >
        <Spin size="large" />
      </div>
    );
  }

  if (state.error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Не удалось загрузить список справочников"
        description={state.error}
        data-testid="dictionaries-load-error"
        action={
          <Button size="small" onClick={() => void load()} data-testid="dictionaries-reload-btn">
            Повторить
          </Button>
        }
      />
    );
  }

  const items = state.items ?? [];

  return (
    <div data-testid="dictionaries-page">
      <div className={styles.pageHeader}>
        <h2 className={styles.pageTitle}>
          Справочники
        </h2>
        <div className={styles.spacer} />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setModalOpen(true)}
          data-testid="dict-create-btn"
        >
          Создать справочник
        </Button>
      </div>

      {items.length === 0 ? (
        <div className={styles.emptyState} data-testid="dictionaries-empty">
          <div className={styles.emptyIcon}>
            <BookOutlined />
          </div>
          <div className={styles.emptyTitle}>
            Справочники пока не созданы
          </div>
          <div className={styles.emptySubtitle}>
            Справочники хранят переиспользуемые списки: команды, люди, технологии
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalOpen(true)}
          >
            Создать первый справочник
          </Button>
        </div>
      ) : (
        <div className={styles.grid} data-testid="dictionaries-list">
          {items.map((item) => (
            <Card
              key={item.id}
              className={styles.card}
              onClick={() => navigate(`/dictionaries/${item.id}`)}
            >
              <div className={styles.cardIcon}>
                <BookOutlined />
              </div>
              <div className={styles.cardName}>{item.name}</div>
              <div className={styles.cardMeta}>
                <span>
                  <TableOutlined style={{ marginRight: 4 }} />
                  {item.columnCount} {item.columnCount === 1 ? 'колонка' : 'колонок'}
                </span>
                <span>
                  <UnorderedListOutlined style={{ marginRight: 4 }} />
                  {item.entryCount} {item.entryCount === 1 ? 'запись' : 'записей'}
                </span>
              </div>
              {item.description ? (
                <p className={styles.cardDescription}>{item.description}</p>
              ) : null}
              <Popconfirm
                title="Удалить справочник?"
                description="Это действие необратимо."
                okText="Удалить"
                cancelText="Отмена"
                okButtonProps={{ danger: true }}
                onConfirm={(e) => { e?.stopPropagation(); void handleDelete(item.id); }}
                onCancel={(e) => e?.stopPropagation()}
              >
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deleting === item.id}
                  className={styles.deleteBtn}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`dict-delete-${item.id}`}
                  aria-label="Удалить справочник"
                />
              </Popconfirm>
            </Card>
          ))}
        </div>
      )}

      <Modal open={modalOpen} onCancel={closeModal} title="Создать справочник" footer={null} destroyOnHidden>
        <Form<CreateDictionaryRequest> form={form} layout="vertical" onFinish={(values) => void handleCreate(values)}>
          <Form.Item
            label="Название"
            name="name"
            rules={[{ required: true, message: 'Введите название справочника' }]}
          >
            <Input data-testid="dict-create-name-input" />
          </Form.Item>
          <Form.Item label="Описание" name="description">
            <Input.TextArea rows={3} data-testid="dict-create-description-input" />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={closeModal}>Отмена</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              data-testid="dict-create-submit"
            >
              Создать
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
